/**
 * The API clients for the app: the clinical client and the practice billing group of the core client, one of each
 * per session, the organization in scope sent on every call (api-v2.md section 4, Q-87), and a small load hook.
 * The only thing kept outside React state is the chosen organization and location id, in sessionStorage: never a
 * patient, an encounter or any record.
 */
import { createApiClient } from "@allergy123/api-client";
import { createClinicalClient, type ClinicalClient } from "@allergy123/api-client/clinical";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const ORGANIZATION_KEY = "a123.clinical.organization";
const LOCATION_KEY = "a123.clinical.location";

const stored = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};
const store = (key: string, value: string | undefined) => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // Private windows may refuse storage; the choice then lives for the page only.
  }
};

let organizationId = stored(ORGANIZATION_KEY);
export const getOrganizationId = () => organizationId;
export function setOrganizationId(id: string | undefined) {
  organizationId = id;
  store(ORGANIZATION_KEY, id);
}
export const getStoredLocationId = () => stored(LOCATION_KEY);
export const storeLocationId = (id: string | undefined) => store(LOCATION_KEY, id);

/** The practice's billing view (ADR 0008): summary, supply balances and orders, served by the core API under the same organization header. */
export type BillingClient = ReturnType<typeof createApiClient>["billing"];

interface Clients {
  clinical: ClinicalClient;
  billing: BillingClient;
}

const Ctx = createContext<Clients | null>(null);

export function ClinicalProvider({ baseUrl, token, children }: { baseUrl: string; token: () => Promise<string | null>; children: ReactNode }) {
  const clients = useMemo<Clients>(() => {
    const config = { baseUrl, token, organizationId: () => organizationId };
    return { clinical: createClinicalClient(config), billing: createApiClient(config).billing };
  }, [baseUrl, token]);
  return <Ctx.Provider value={clients}>{children}</Ctx.Provider>;
}

function useClients(): Clients {
  const c = useContext(Ctx);
  if (!c) throw new Error("useClinical must be used inside <ClinicalProvider>");
  return c;
}

export function useClinical(): ClinicalClient {
  return useClients().clinical;
}

/** The billing routes (`/billing/*` on the core API). Reading supplies needs `clinical:read`; the summary, orders and every write need `clinical:manage`. */
export function useBilling(): BillingClient {
  return useClients().billing;
}

export interface Loaded<T> {
  state: "loading" | "ready" | "error";
  data?: T;
  error?: Error;
  reload: () => void;
}

/** Loads once per dependency change and exposes reload; a reload keeps the previous data on screen until the new one lands. */
export function useLoad<T>(load: (api: ClinicalClient) => Promise<T>, deps: unknown[] = []): Loaded<T> {
  const api = useClinical();
  const [tick, setTick] = useState(0);
  const [result, setResult] = useState<Omit<Loaded<T>, "reload">>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setResult((r) => (r.state === "ready" ? r : { state: "loading" }));
    load(api)
      .then((data) => live && setResult({ state: "ready", data }))
      .catch((e: unknown) => live && setResult({ state: "error", error: e instanceof Error ? e : new Error(String(e)) }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tick, ...deps]);
  return { ...result, reload: () => setTick((t) => t + 1) };
}
